/**
 * ============================================================================
 * [P3-05-C] BackupActions - 백업/복원 액션 버튼 컴포넌트
 *
 * 기능:
 * - 백업 다운로드 버튼
 * - 복원 파일 선택 버튼
 * - 처리 중 상태 표시
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useRef, useCallback } from 'react'
import styles from './Backup.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface BackupActionsProps {
  onBackup: () => void
  onRestore: (file: File) => void
  isProcessing: boolean
}

// ============================================================================
// 3. BackupActions Component
// ============================================================================
export function BackupActions({
  onBackup,
  onRestore,
  isProcessing,
}: BackupActionsProps) {
  // ========================================================================
  // 3-1. 파일 입력 ref
  // ========================================================================
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ========================================================================
  // 3-2. 복원 버튼 클릭 핸들러 (파일 선택 트리거)
  // ========================================================================
  const handleRestoreClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // ========================================================================
  // 3-3. 파일 선택 핸들러
  // ========================================================================
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onRestore(file)
    }
    // 같은 파일 재선택 가능하도록 초기화
    e.target.value = ''
  }, [onRestore])

  // ========================================================================
  // 3-4. Render
  // ========================================================================
  return (
    <div className={styles.backupActions}>
      {/* ================================================================
          백업 다운로드 버튼
          ================================================================ */}
      <button
        className={styles.backupBtn}
        onClick={onBackup}
        disabled={isProcessing}
        aria-label="백업 다운로드"
      >
        {isProcessing ? (
          <>
            <span className={styles.spinner} />
            처리 중...
          </>
        ) : (
          '백업 다운로드'
        )}
      </button>

      {/* ================================================================
          복원 버튼
          ================================================================ */}
      <button
        className={styles.restoreBtn}
        onClick={handleRestoreClick}
        disabled={isProcessing}
        aria-label="백업 복원"
      >
        백업 복원
      </button>

      {/* ================================================================
          숨겨진 파일 입력
          ================================================================ */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
    </div>
  )
}

export default BackupActions
